import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
    ErrorMessage,
    StyledGiftCardContainer,
    StyledGiftCardStatusContainer,
    StyledGiftCardWrapper,
    StyledGiftCardBalancePageDescription,
    StyledGiftCardBalancePageHeading,
    StyledGrid,
} from './styled';
import { GiftCardForm } from './components/GiftCardForm/GiftCardForm';
import { GiftCardStatus } from './components/GiftCardStatus/GiftCardStatus';
import { GiftCardFormFields } from './models/fields';
import { useGiftCertificates } from '~/features/gift-card-balance/hooks/useGiftCertificates';
import { GiftCardBalanceSuccess } from './models/giftcard';
import { Container } from '~/shared/components/Container';
import { RawHtml } from '~/shared/components/RawHtml';
import { useTranslation } from '~/shared/utils/translation';

type status = 'loading' | 'error' | 'success' | null;

type Props = {
    headline?: string;
    description?: string;
};

type Variant = {
    opacity: number;
    scale: number;
    position: 'absolute' | 'relative';
};

type Variants = {
    visible: Variant;
    hidden: Variant;
};

enum ErrorType {
    GC_NO_FUNDS_AVAILABLE = 'GC_NO_FUNDS_AVAILABLE',
}

const transition = { duration: 0.2, ease: 'easeInOut' };
const variants: Variants = {
    visible: { opacity: 1, scale: 1, position: 'relative' },
    hidden: { opacity: 0, scale: 0.9, position: 'absolute' },
};

const getZeroBalance = (cardNo: string): GiftCardBalanceSuccess => ({
    balance: 0,
    certificateNumber: cardNo,
    expirationDate: null,
    isValid: false,
});

export const GiftCardBalance = ({ headline, description }: Props) => {
    const { translate } = useTranslation();
    const { getBalance } = useGiftCertificates();
    const clearState = () => setState({ status: null });
    const [state, setState] = useState<{
        status?: status;
        data?: GiftCardBalanceSuccess;
        error?: string;
    }>({
        status: null,
    });

    const handleSubmit = async ({ cardNo, cvcNo }: GiftCardFormFields) => {
        setState({ status: 'loading' });

        try {
            const response = await getBalance(cardNo, cvcNo);
            setState({ status: 'success', data: response });
            window.scrollTo(0, 0);
        } catch (error) {
            const errorMessage = (error as Error).message;
            if (errorMessage === ErrorType.GC_NO_FUNDS_AVAILABLE) {
                setState({ data: getZeroBalance(cardNo) });
                return;
            }
            setState({ status: 'error', error: errorMessage });
        }
    };

    return (
        <StyledGiftCardWrapper>
            <AnimatePresence>
                {!state.data ? (
                    <>
                        <Container hasVerticalGutter hasGutter>
                            <StyledGiftCardContainer
                                key="form"
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                                variants={variants}
                                transition={transition}
                            >
                                <StyledGrid flow="row" columns="1fr 1fr" columnGap="extrasmall">
                                    <div>
                                        <div>
                                            <StyledGiftCardBalancePageHeading>
                                                {headline}
                                            </StyledGiftCardBalancePageHeading>
                                            <StyledGiftCardBalancePageDescription>
                                                <RawHtml html={description} />
                                            </StyledGiftCardBalancePageDescription>
                                        </div>
                                        <GiftCardForm
                                            onSubmit={handleSubmit}
                                            isLoading={state.status === 'loading'}
                                        />
                                        {state.status === 'error' && (
                                            <ErrorMessage>
                                                {translate(
                                                    'giftCardBalance.giftCardFormErrorMessage',
                                                )}
                                            </ErrorMessage>
                                        )}
                                    </div>
                                </StyledGrid>
                            </StyledGiftCardContainer>
                        </Container>
                    </>
                ) : (
                    <StyledGiftCardStatusContainer
                        key="status"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={variants}
                        transition={transition}
                    >
                        <GiftCardStatus {...state.data} clearState={clearState} />
                    </StyledGiftCardStatusContainer>
                )}
            </AnimatePresence>
        </StyledGiftCardWrapper>
    );
};
